import React, { Component, lazy, Suspense, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { HoiPoiProvider } from '@Sage/hoi-poi-ui';
import { AuthenticationPage } from 'containers';
import AuthorizedRoute from 'components/authorized-route';
import AppLoader from 'components/AppLoader';
import { LiteralsActions, PermissionsActions } from 'actions';
import { setupMomentLocale } from 'utils/datetime';
import { ALLOW_LANG } from 'constants/App';
import DevPopUp from 'components/DevPopUp';
import { sanitizeEntityToGo } from 'utils/authCallback';

import PageLoader from '../../components/PageLoader';

const ChooseImplementationPage = lazy(() => import('containers/choose-implementation-page'));
const DefaultPage = lazy(() => import('containers/default-page'));

function mapStateToProps({ implementations, authentication, literal }) {
    return {
        currentImpl: implementations.current || {},
        user: authentication.user || {},
        canSwitchImpl: authentication.canSwitchImpl,
        impl: authentication.impl,
        literalsLoading: literal.loading || Object.keys(literal.literals).length === 0,
    };
}

const mapDispatchToProps = (dispatch) => ({
    getLiterals: bindActionCreators(LiteralsActions, dispatch).getLiterals,
    getPermissions: bindActionCreators(PermissionsActions, dispatch).getPermissions,
});

class App extends Component {
    constructor(props) {
        super();
        const code = (window.location.search.match(/code=([^&]+)/) || [])[1];
        const state = (window.location.search.match(/state=([^&]+)/) || [])[1];
        const token = (window.location.search.match(/token=([^&]+)/) || [])[1];
        const params = new URLSearchParams(window.location.search);
        const entityToGo = sanitizeEntityToGo(params.get('entityToGo'));

        if (code && state) {
            window.location.replace(
                `${window.location.origin}#/auth/login/?oauthUrl=${encodeURIComponent(
                    window.location.href,
                )}&state=${state}`,
            );
        }

        if (token) {
            let url = `${window.location.origin}#/auth/login/?token=${token}`;
            if (entityToGo) url += `&entityToGo=${entityToGo}`;

            window.location.replace(url);
        }
    }

    componentDidMount() {
        const { user, impl, getLiterals, history, canSwitchImpl } = this.props;
        const userLang =
            user && user.lang ? user.lang : navigator.language || navigator.userLanguage;
        const lang = ALLOW_LANG.includes(userLang) ? userLang : 'en';
        const implId = impl != null ? impl : -1;

        getLiterals(lang, implId);
        setupMomentLocale(lang);
        if (canSwitchImpl && !impl) {
            history.push('/choose');
        }
    }

    render() {
        const { literalsLoading } = this.props;
        if (literalsLoading) return <AppLoader />;

        return (
            <Suspense fallback={<PageLoader />}>
                <HoiPoiProvider>
                    <Routes>
                        <Route path="/auth/login/*" element={<AuthenticationPage />} />
                        <Route
                            path="/choose"
                            element={
                                <AuthorizedRoute>
                                    <ChooseImplementationPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route
                            path="/app/*"
                            element={
                                <AuthorizedRoute>
                                    <DefaultPage />
                                </AuthorizedRoute>
                            }
                        />
                        <Route path="*" element={<Navigate to="/auth/login" replace />} />
                    </Routes>
                    <DevPopUp />
                </HoiPoiProvider>
            </Suspense>
        );
    }
}

App.propTypes = {
    currentImpl: PropTypes.shape({}),
    user: PropTypes.shape({}),
    literalsLoading: PropTypes.bool,
    getLiterals: PropTypes.func.isRequired,
    getPermissions: PropTypes.func,
    impl: PropTypes.number,
    history: PropTypes.shape({}).isRequired,
};

const ConnectedApp = connect(mapStateToProps, mapDispatchToProps)(App);

const AppWithRouter = (props) => {
    const navigate = useNavigate();
    const history = useMemo(
        () => ({
            push: (path) => navigate(path),
            replace: (path) => navigate(path, { replace: true }),
        }),
        [navigate],
    );

    return <ConnectedApp {...props} history={history} />;
};

export default AppWithRouter;
